import { IPCEventType } from '@electron/shared';

import { ElectronAPI } from '../base/ElectronAPI';
import { type ElectronListenerMethod } from '../base/types';

import type { HummingbirdThreadPayload } from '@electron/shared/hummingbird.types';

const Commands = {
  GetHummingbirdEnabled: 'get-hummingbird-enabled',
  SetHummingbirdEnabled: 'set-hummingbird-enabled',
  SetHummingbirdMouseEvents: 'set-hb-mouse-events',
  HideHummingbird: 'hide-hummingbird',
  ExpandThreadToMainWindow: 'expand-thread-to-main-window',
  OpenThreadInHummingbird: 'open-thread-in-hummingbird',
  ShowRecentChatsMenu: 'show-recent-chats-menu',
  GetHummingbirdShortcut: 'get-hummingbird-shortcut',
  SetHummingbirdShortcut: 'set-hummingbird-shortcut',
  SetHummingbirdPinned: 'set-hummingbird-pinned',
  DetachMove: 'hummingbird-detach-move',
  GetVoiceShortcutEnabled: 'get-voice-shortcut-enabled',
  SetVoiceShortcutEnabled: 'set-voice-shortcut-enabled',
  GetVoiceAutoSendEnabled: 'get-hummingbird-voice-auto-send',
  SetVoiceAutoSendEnabled: 'set-hummingbird-voice-auto-send',
  GetHideFromScreenShare: 'get-hummingbird-hide-from-screen-share',
  SetHideFromScreenShare: 'set-hummingbird-hide-from-screen-share',
} as const;

const TRAVERSAL_TIMEOUT_MS = 4000;

export class HummingbirdAPI extends ElectronAPI {
  getHummingbirdEnabled(): Promise<boolean> {
    return this.invoke<boolean>(Commands.GetHummingbirdEnabled);
  }

  async setHummingbirdEnabled(enabled: boolean): Promise<void> {
    return this.invoke<void>(Commands.SetHummingbirdEnabled, { enabled });
  }

  async ignoreMouseEvents(ignore: boolean): Promise<void> {
    return this.invoke<void>(Commands.SetHummingbirdMouseEvents, { ignore });
  }
  async hideHummingbird(): Promise<void> {
    return this.invoke<void>(Commands.HideHummingbird);
  }

  captureAppContext({
    pid,
    windowId,
    traverseAllWindows,
  }: {
    pid: number;
    windowId?: number;
    traverseAllWindows?: boolean;
  }) {
    return this.requestContextKit(
      IPCEventType.traverseApp,
      { pid, windowId, traverseAllWindows },
      {
        responseType: IPCEventType.traversedAppContent,
        timeout: TRAVERSAL_TIMEOUT_MS,
      }
    );
  }

  async expandThreadToMainWindow({
    threadId,
    draftContent,
    chatMode,
    draftContext,
  }: HummingbirdThreadPayload) {
    return this.invoke<void>(Commands.ExpandThreadToMainWindow, {
      threadId,
      draftContent,
      chatMode,
      draftContext,
    });
  }

  async openThreadInHummingbird({
    threadId,
    draftContent,
    chatMode,
    draftContext,
  }: HummingbirdThreadPayload) {
    return this.invoke<void>(Commands.OpenThreadInHummingbird, {
      threadId,
      draftContent,
      chatMode,
      draftContext,
    });
  }

  async showRecentChatsMenu(items: Array<{ id: string; title: string }>) {
    return this.invoke<void>(Commands.ShowRecentChatsMenu, { items });
  }

  listenToHummingbirdWindowBecomeKey: ElectronListenerMethod<IPCEventType.hummingbirdBecomeKeyWindow> =
    (callback) => {
      return this.addListener({
        eventType: IPCEventType.hummingbirdBecomeKeyWindow,
        callback,
      });
    };

  getHummingbirdShortcut(): Promise<string> {
    return this.invoke<string>(Commands.GetHummingbirdShortcut);
  }

  async setHummingbirdShortcut(shortcut: string): Promise<void> {
    return this.invoke<void>(Commands.SetHummingbirdShortcut, { shortcut });
  }

  async setHummingbirdPinned(pinned: boolean): Promise<void> {
    return this.invoke<void>(Commands.SetHummingbirdPinned, { pinned });
  }

  /**
   * Report the floating unit's global-screen center so the main process can
   * relocate the overlay window when the drag crosses onto another display.
   * Fire-and-forget: high-frequency and never awaited.
   */
  detachMove(point: { globalX: number; globalY: number }): void {
    this.ipcRenderer.send(Commands.DetachMove, point);
  }

  getVoiceShortcutEnabled(): Promise<boolean> {
    return this.invoke<boolean>(Commands.GetVoiceShortcutEnabled);
  }

  async setVoiceShortcutEnabled(enabled: boolean): Promise<void> {
    return this.invoke<void>(Commands.SetVoiceShortcutEnabled, { enabled });
  }

  getVoiceAutoSendEnabled(): Promise<boolean> {
    return this.invoke<boolean>(Commands.GetVoiceAutoSendEnabled);
  }

  async setVoiceAutoSendEnabled(enabled: boolean): Promise<void> {
    return this.invoke<void>(Commands.SetVoiceAutoSendEnabled, { enabled });
  }

  getHideFromScreenShare(): Promise<boolean> {
    return this.invoke<boolean>(Commands.GetHideFromScreenShare);
  }

  async setHideFromScreenShare(hidden: boolean): Promise<void> {
    return this.invoke<void>(Commands.SetHideFromScreenShare, {
      hidden,
    });
  }
}
